import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { ReactElement, UIEvent } from 'react';

import Enums from '@core/types/api/Enums';
import type { List } from '@core/types/list';

import { useSlabQuery } from '../../../hooks/useSlabQuery';
import type { Column } from '../TableDataModel';
import type { TypeAheadTables } from './types';

interface Props {
  filterValues: any[];
  onFilterChange: (option: string, checked: boolean) => void;
  onFilterClear: () => void;
  isFilterOpen: boolean;
  typeaheadTableName: TypeAheadTables;
  column: Column<any>;
}

const itemsPerPage = 10;

export const InColumnFilter = ({
  filterValues,
  onFilterChange,
  onFilterClear,
  isFilterOpen,
  typeaheadTableName,
  column,
}: Props): ReactElement => {
  const [page, setPage] = useState(1);

  const theme = useTheme();

  const { isLoading, isError, data, refetch, isFetching } = useSlabQuery(
    `GET ${typeaheadTableName} typeahead`,
    {
      queryParams: {
        isDistinct: true,
        columns: [column.id as any],
        sortBy: {
          name: column.id as any,
          direction: Enums.SortDirection.Ascending,
        },
        perPage: itemsPerPage,
        page: page - 1,
      },
    },
    { enabled: isFilterOpen },
  );

  const items = (data as List<string> | null)?.items ?? [];
  const count = (data as List<string> | null)?.count ?? 0;

  const [allOptions, setAllOptions] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (!isFilterOpen) {
      setAllOptions([]);
      setPage(1);
      setHasMore(true);
    }
  }, [isFilterOpen, column.id, typeaheadTableName]);

  useEffect(() => {
    if (isFilterOpen && items.length > 0) {
      setAllOptions((prev) => {
        const newOptions = items.filter((it) => !prev.includes(it));
        return [...prev, ...newOptions];
      });
      if (allOptions.length + items.length >= count) {
        setHasMore(false);
      }
    }
  }, [items, isFilterOpen]);

  const handleScroll = (e: UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (hasMore && !isLoading && !isFetching && scrollHeight - scrollTop - clientHeight < 32) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (isFilterOpen && page > 1 && hasMore) {
      refetch();
    }
  }, [page]);

  const filteredOptions = allOptions.filter((option) => option !== '');

  return (
    <Box display='flex' flexDirection='column' alignItems='center' flexWrap='nowrap'>
      {isLoading && allOptions.length === 0 && (
        <Box display='flex' justifyContent='center' width='100%' py={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isError && (
        <Box textAlign='center' py={2} color='error.main'>
          Failed to load options
        </Box>
      )}

      {!isError && filteredOptions.length > 0 && (
        <FormGroup
          sx={{
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            overflowX: 'visible',
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            '&::-webkit-scrollbar': {
              height: 'auto',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.divider,
              borderRadius: '4px',
            },
          }}
          onScroll={handleScroll}
        >
          {filteredOptions.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={filterValues.includes(option)}
                  onChange={(e) => onFilterChange(option, e.target.checked)}
                  size='small'
                />
              }
              label={
                <Tooltip title={option}>
                  <Typography
                    variant='body2'
                    noWrap
                    sx={{
                      maxWidth: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option}
                  </Typography>
                </Tooltip>
              }
            />
          ))}
          {!isError && filteredOptions.length === 0 && (
            <Box textAlign='center' py={1}>
              No options available
            </Box>
          )}
          {isFetching && (
            <Box display='flex' justifyContent='center' width='100%' py={1}>
              <CircularProgress size={18} />
            </Box>
          )}
        </FormGroup>
      )}

      <Divider sx={{ width: '100%', marginY: 2 }} />
      <Box display='flex' width='100%' justifyContent='flex-end'>
        <Button onClick={onFilterClear} size='small' disabled={filterValues.length === 0}>
          Clear
        </Button>
      </Box>
    </Box>
  );
};
